import { useState } from 'react';
import { InfoBanner, WarningBanner } from 'ui/banner';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { FormField } from 'ui/formField';
import { AddNew16 } from 'ui/icons/aurora';
import { InputText, TextArea } from 'ui/inputs';
import { StyledLink } from 'ui/links';
import { Modal, ModalFooter, ModalHeader } from 'ui/modal';
import { localizeDateTime, localizeNumber, DateFormat, TimeFormat } from 'utils/l10n';

import { AIQuerySummaryInputButton } from './AIQuerySummaryInputButton';
import { StyledInputWrapper, StyledModalBody } from '../Styled';
import { StyledInputLabelOptional, StyledListWrapper, StyledSplitButton } from './Styled';
import { useListId, useSaveAsNewListMutation, useTranslation } from '../../../hooks';
import { useModalsContext } from '../../../contexts/modals';
import { useSegmentationQueryContext } from '../../../contexts/segmentationQuery';
import { NewSubListRequest, SaveAsNewListParams } from '../../../types';
import { usePermissionsContext } from '../../../contexts/permissions';
import { useListDetailsContext } from '../../../contexts/listDetails';
import { useDisplayNewStaticListToast } from './useDisplayNewStaticListToast';

const listLimit = 5000000;

export const testIds = {
  bannerInfo: 'segmentation-nsl-banner-info',
  bannerWarnDuplicate: 'segmentation-nsl-banner-duplicate',
  bannerWarnAggregate: 'segmentation-nsl-banner-split',
  buttonSave: 'segmentation-nsl-btn-save',
  buttonSplit: 'segmentation-nsl-btn-split',
  inputDescription: 'segmentation-nsl-input-desc',
  inputName: 'segmentation-nsl-input-name',
  inputSize: 'segmentation-nsl-input-size',
  inputSizeError: 'segmentation-nsl-input-size-error',
  linkRemove: 'segmentation-nsl-link-remove',
  modal: 'segmentation-nsl',
  sublist: 'segmentation-nsl-sublist',
};

export const NewStaticListsModal = () => {
  const { t } = useTranslation();
  const listId = useListId();
  const { list } = useListDetailsContext();
  const { canUseSegmentationAiDescriptions } = usePermissionsContext();
  const { count: listSize = 0, searchQuery } = useSegmentationQueryContext();
  const { hideModal: closeModal } = useModalsContext();
  const { mutate: saveAsNewLists, isLoading } = useSaveAsNewListMutation();
  const displayNewStaticListToast = useDisplayNewStaticListToast();

  const makeNewList = (size: number, count: number) => ({
    description: '',
    name: list?.name
      ? t('modal.newStaticLists.field.listName.value', {
          name: list.name,
          count,
        })
      : t('modal.newStaticLists.field.listName.defaultValue', {
          dateTime: localizeDateTime(new Date(), {
            dateStyle: DateFormat.Short,
            showTime: true,
            timeStyle: TimeFormat.TwelveHour,
          }),
          count,
        }),
    size,
  });

  const [lists, setLists] = useState<NewSubListRequest[]>([makeNewList(listSize, 1)]);
  const [autoSize, setAutoSize] = useState<boolean>(true);

  const listSizesOk = lists.every((lst) => lst.size <= listLimit && lst.size >= 0);
  const totalSize = lists.reduce((ttl, lst) => ttl + lst.size, 0);
  const totalSizeOk = totalSize <= listLimit;
  const listNames = lists.map((lst) => lst.name);
  const duplicateNames = listNames.filter((name, idx) => listNames.indexOf(name) !== idx);
  const hasDuplicateNames = !!duplicateNames.length;
  const canSubmit =
    !hasDuplicateNames &&
    listSizesOk &&
    totalSizeOk &&
    lists.every((lst) => lst.name.trim().length);

  const setValue = (key: keyof NewSubListRequest, val: string | number, idx: number) => {
    setLists((l) => [...l.slice(0, idx), { ...l[idx], [key]: val }, ...l.slice(idx + 1, l.length)]);
  };

  const removeList = (idx: number) => {
    setLists((l) => {
      if (autoSize) {
        const newSize = l.length - 1;
        const sizeEach = Math.floor(listSize / newSize);
        const sizeLast = sizeEach + (listSize % sizeEach);
        const getSize = (i: number, len: number) => (i < len - 1 ? sizeEach : sizeLast);
        return [
          ...l.slice(0, idx).map((lst, i) => ({ ...lst, size: getSize(i, newSize) })),
          ...l
            .slice(idx + 1, l.length)
            .map((lst, i, ary) => ({ ...lst, size: getSize(i, ary.length) })),
        ];
      }
      return [...l.slice(0, idx), ...l.slice(idx + 1, l.length)];
    });
  };

  const addList = () => {
    setLists((l) => {
      const updatedListCount = l.length + 1;
      if (autoSize) {
        const sizeEach = Math.floor(listSize / updatedListCount);
        const sizeLast = sizeEach + (listSize % sizeEach);
        return [
          ...l.map((lst) => ({ ...lst, size: sizeEach })),
          makeNewList(sizeLast, updatedListCount),
        ];
      }
      return [...l, makeNewList(Math.max(listSize - totalSize, 0), updatedListCount)];
    });
  };

  const addStaticLists = () => {
    if (isLoading) return;
    const params: SaveAsNewListParams = {
      description: null,
      emailListId: listId, // required for creating static lists from other lists
      isDynamic: false,
      name: '',
      searchQuery,
      subLists: lists,
    };

    saveAsNewLists(params);
    displayNewStaticListToast();
    closeModal();
  };

  return (
    <Modal id={testIds.modal} onClose={closeModal} isModalOpen>
      <ModalHeader>{t('modal.newStaticLists.header')}</ModalHeader>
      <StyledModalBody>
        {lists.map((list, idx) => (
          <StyledListWrapper
            key={`list_${idx}`}
            data-test={testIds.sublist}
            {...(lists.length > 1 ? { className: 'multiList' } : {})}
          >
            <StyledInputWrapper>
              {/* work-around for ordinal in i18n < 23.0.0, update here and strings after version update */}
              <FormField
                hasError={!list.name.trim().length || duplicateNames.includes(list.name)}
                labelText={
                  lists.length === 1
                    ? t('modal.newStaticLists.field.listName.label')
                    : t('modal.newStaticLists.field.listName.label', {
                        count: idx + 1,
                        ordinal: true,
                      })
                }
                restrictionText={`${list.name.length}/255`}
              >
                <InputText
                  data-test={testIds.inputName}
                  hasIcon
                  maxLength={255}
                  onChange={(newVal) => setValue('name', newVal, idx)}
                  value={list.name}
                />
              </FormField>
            </StyledInputWrapper>
            <StyledInputWrapper>
              <FormField
                labelText={
                  <>
                    {t('modal.newStaticLists.field.description.label')}&nbsp;
                    <StyledInputLabelOptional>
                      {t('modal.newStaticLists.field.optional')}
                    </StyledInputLabelOptional>
                  </>
                }
                restrictionText={`${list.description?.length || 0}/255`}
              >
                <TextArea
                  data-test={testIds.inputDescription}
                  maxLength={255}
                  onChange={(newVal) => setValue('description', newVal, idx)}
                  placeholder={t('modal.newStaticLists.field.description.placeholder')}
                  rows={3}
                  value={list.description || ''}
                  {...(canUseSegmentationAiDescriptions
                    ? {
                        Suffix: (
                          <AIQuerySummaryInputButton
                            onCommit={(newVal: string) => setValue('description', newVal, idx)}
                            query={searchQuery}
                          />
                        ),
                      }
                    : {})}
                />
              </FormField>
            </StyledInputWrapper>
            <StyledInputWrapper>
              <FormField
                labelText={t('modal.newStaticLists.field.listSize.label')}
                hasError={list.size > listLimit}
                errorText={t('modal.newStaticLists.field.listSize.errorSize')}
              >
                <InputText
                  data-test={testIds.inputSize}
                  hasIcon
                  onChange={(newVal) => {
                    setValue('size', parseInt(newVal, 10) || '', idx);
                    setAutoSize(false);
                  }}
                  value={list.size.toString() || ''}
                />
              </FormField>
            </StyledInputWrapper>
            {lists.length > 1 && (
              <StyledLink
                variant="monochrome"
                size="caption"
                onClick={() => removeList(idx)}
                data-test={testIds.linkRemove}
              >
                {t('modal.newStaticLists.link.remove')}
              </StyledLink>
            )}
          </StyledListWrapper>
        ))}
        {totalSizeOk ? (
          <InfoBanner data-test={testIds.bannerInfo}>
            {lists.length > 1
              ? t('modal.newStaticLists.banner.info.newListsMultiple', { listCount: lists.length })
              : t('modal.newStaticLists.banner.info.newLists', {
                  listLimit: localizeNumber(listLimit),
                })}
          </InfoBanner>
        ) : (
          <WarningBanner data-test={testIds.bannerWarnAggregate}>
            {t('modal.newStaticLists.banner.warning.aggregateTooLarge', {
              aggregate: localizeNumber(totalSize),
              listLimit: localizeNumber(listLimit),
            })}
          </WarningBanner>
        )}
        {hasDuplicateNames && (
          <WarningBanner data-test={testIds.bannerWarnDuplicate}>
            {t('modal.newStaticLists.banner.warning.uniqueNames')}
          </WarningBanner>
        )}
        <StyledSplitButton
          data-test={testIds.buttonSplit}
          onClick={addList}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          <AddNew16 />
          {t('modal.newStaticLists.button.split')}
        </StyledSplitButton>
      </StyledModalBody>
      <ModalFooter>
        <Button
          isDisabled={!canSubmit}
          onClick={addStaticLists}
          size={BUTTON_SIZES.LARGE}
          data-test={testIds.buttonSave}
        >
          {t('modal.newStaticLists.button.save')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
